import { useEffect, useRef } from 'react';

import { TABBABLE_ELEMENTS_SELECTOR } from 'constants/selectors';

const getFocusableElements = container => {
  if (container instanceof HTMLElement) {
    const focusableElements = container.querySelectorAll(TABBABLE_ELEMENTS_SELECTOR);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    return {
      focusableElements,
      firstFocusableElement,
      lastFocusableElement
    };
  } else {
    return {};
  }
};

const trap = ({ ref, shouldFocusFirstElement }) => {
  const el = ref.current;
  const returnFocusElement = document.activeElement;

  if (shouldFocusFirstElement) {
    const { firstFocusableElement } = getFocusableElements(el);
    firstFocusableElement && firstFocusableElement.focus();
  }

  const handleKeydown = e => {
    const isTabPressed = e.key === 'Tab';

    if (!isTabPressed) {
      return;
    }

    const { firstFocusableElement, lastFocusableElement } = getFocusableElements(el);
    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  };

  el.addEventListener('keydown', handleKeydown);

  return () => {
    returnFocusElement.focus();
    el.removeEventListener('keydown', handleKeydown);
  };
};

const useFocusTrap = ({ shouldFocusFirstElement }) => {
  const ref = useRef(null);

  useEffect(() => trap({ ref, shouldFocusFirstElement }), [shouldFocusFirstElement]);

  return ref;
};

export default useFocusTrap;
